import { isDebugEnabled } from '@euler/functions/debug';
import { makeDebug } from '@euler/utils';
import {
  useBehaviorSubject,
  useBehaviorSubjectUpdater,
  useCreation,
} from '@euler/utils/hooks';
import { StatusBar, StatusBarStyle } from 'expo-status-bar';
import {
  ReactNode,
  createContext,
  memo,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { BehaviorSubject } from 'rxjs';

const debug = makeDebug('app-status-bar', isDebugEnabled('statusBar'));

type AppStatusBarContextProps = {
  name: string;
  barStyles$: BehaviorSubject<StatusBarStyle[]>;
};

export const AppStatusBarContext = createContext<AppStatusBarContextProps>(
  null as any,
);

export const AppStatusBar = () => {
  const { barStyles$ } = useContext(AppStatusBarContext);
  const [barStyles] = useBehaviorSubject(barStyles$);
  const barStyle = barStyles[barStyles.length - 1];
  return <StatusBar style={barStyle ?? 'light'} />;
};

const kDefaultBarStyles$ = new BehaviorSubject<StatusBarStyle[]>([]);

const useCurrentStatusBarStyle = (): StatusBarStyle => {
  const context = useContext(AppStatusBarContext);
  const [barStyles] = useBehaviorSubject(
    context?.barStyles$ ?? kDefaultBarStyles$,
  );
  return barStyles[barStyles.length - 1] ?? 'light';
};

export const useStatusBarStyle = (barStyle: StatusBarStyle, enabled = true) => {
  const { name, barStyles$ } = useContext(AppStatusBarContext);
  const updateBarStyles = useBehaviorSubjectUpdater(barStyles$);
  debug(
    '🌈 [%s] request status bar style to %s (current: %s)',
    name,
    barStyle,
    barStyles$.value[barStyles$.value.length - 1],
  );
  useEffect(() => {
    if (!enabled) return;
    const currentBarStyle = barStyles$.value[barStyles$.value.length - 1];
    if (currentBarStyle === barStyle) {
      debug('🟡 [%s] current bar style: %s, no change', name, currentBarStyle);
      return;
    }

    debug('✅ [%s] status bar style changed to %s', name, barStyle);
    updateBarStyles(barStyles => [...barStyles, barStyle]);

    return () => {
      updateBarStyles(barStyles => barStyles.slice(0, barStyles.length - 1));
      debug('🟢 [%s] status bar style reset to %s', name, currentBarStyle);
    };
  }, [name, barStyle, barStyles$, updateBarStyles, enabled]);
};

export const AppStatusBarContextProvider = memo(
  ({ name, children }: { name: string; children?: ReactNode }) => {
    const currentBarStyle = useCurrentStatusBarStyle();

    const barStyles$ = useCreation(
      () => new BehaviorSubject<StatusBarStyle[]>([currentBarStyle]),
      [currentBarStyle],
    );

    const value = useMemo<AppStatusBarContextProps>(
      () => ({ name, barStyles$ }),
      [name, barStyles$],
    );
    return (
      <AppStatusBarContext.Provider value={value}>
        <AppStatusBar />
        {children}
      </AppStatusBarContext.Provider>
    );
  },
);
